<div class="container">

  <header class="page-heading">
    <h1>Home Loan Prepayment Calculator</h1>
    <p>See exactly how much interest you save and how many years you cut off your home loan by making a lump-sum prepayment or paying a little extra every month.</p>
  </header>

  <!-- ══ INPUT CARD ══════════════════════════════════════════════════════ -->
  <div class="card mb-lg prepay-input-card">
    <div class="section-title">Your Loan &amp; Prepayment</div>

    <div class="field-row-2">
      <!-- Outstanding amount -->
      <div class="field-group" [class.has-error]="hasError('outstandingAmount')">
        <label class="field-label" for="outstandingAmount">Outstanding Loan Amount</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="outstandingAmount" type="number"
            [(ngModel)]="outstandingAmount"
            [min]="C.amount.min" [max]="C.amount.max" [step]="C.amount.step"
            (ngModelChange)="onFieldChange('outstandingAmount')"
            (blur)="onFieldBlur('outstandingAmount')"
            placeholder="e.g. 50,00,000" autocomplete="off"/>
        </div>
        @if (hasError('outstandingAmount')) {
          <div class="field-error">{{ fieldError('outstandingAmount') }}</div>
        } @else {
          <div class="field-hint">Your current remaining balance</div>
        }
      </div>

      <!-- Interest rate -->
      <div class="field-group" [class.has-error]="hasError('annualInterestRate')">
        <label class="field-label" for="annualRate">Interest Rate</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualInterestRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualInterestRate')"
            (blur)="onFieldBlur('annualInterestRate')"
            placeholder="e.g. 8.5" autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        @if (hasError('annualInterestRate')) {
          <div class="field-error">{{ fieldError('annualInterestRate') }}</div>
        }
      </div>
    </div>

    <!-- Remaining tenure -->
    <div class="field-group" [class.has-error]="hasError('remainingTermYears')">
      <label class="field-label" for="termYears">Remaining Tenure</label>
      <div class="input-shell suffix">
        <input id="termYears" type="number"
          [(ngModel)]="remainingTermYears"
          [min]="C.term.min" [max]="C.term.max" [step]="C.term.step"
          (ngModelChange)="onFieldChange('remainingTermYears')"
          (blur)="onFieldBlur('remainingTermYears')"
          placeholder="e.g. 20" autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      @if (hasError('remainingTermYears')) {
        <div class="field-error">{{ fieldError('remainingTermYears') }}</div>
      }
    </div>

    <!-- ── PREPAYMENT ─────────────────────────────────────────────────── -->
    <div class="prepay-block">
      <div class="prepay-block-header">
        <span class="prepay-block-title">Prepayment Plan</span>
        <div class="mode-toggle">
          <button type="button" class="mode-btn" [class.active]="prepaymentOption==='reduce_tenure'"
            (click)="prepaymentOption='reduce_tenure'; calculated && calculate()">Reduce Tenure</button>
          <button type="button" class="mode-btn" [class.active]="prepaymentOption==='reduce_emi'"
            (click)="prepaymentOption='reduce_emi'; calculated && calculate()">Reduce EMI</button>
        </div>
      </div>

      <div class="field-row-2 mt-sm">
        <!-- One-time lump sum -->
        <div class="field-group" [class.has-error]="hasError('lumpSumAmount')">
          <label class="field-label" for="lumpSum">One-time Lump-sum <span class="optional-tag">optional</span></label>
          <div class="input-shell prefix">
            <span class="affix">₹</span>
            <input id="lumpSum" type="number"
              [(ngModel)]="lumpSumAmount"
              min="0" step="10000"
              (ngModelChange)="onFieldChange('lumpSumAmount')"
              (blur)="onFieldBlur('lumpSumAmount')"
              placeholder="e.g. 5,00,000" autocomplete="off"/>
          </div>
        </div>

        <!-- At month -->
        <div class="field-group" [class.has-error]="hasError('lumpSumMonth')">
          <label class="field-label" for="lumpMonth">…paid at Month #</label>
          <div class="input-shell prefix">
            <span class="affix">#</span>
            <input id="lumpMonth" type="number"
              [(ngModel)]="lumpSumMonth"
              min="1" [max]="maxMonths || 360" step="1"
              (ngModelChange)="onFieldChange('lumpSumMonth')"
              (blur)="onFieldBlur('lumpSumMonth')"
              placeholder="12" autocomplete="off"/>
          </div>
          @if (hasError('lumpSumMonth')) {
            <div class="field-error">{{ fieldError('lumpSumMonth') }}</div>
          }
        </div>
      </div>

      <!-- Monthly extra -->
      <div class="field-group mt-sm" [class.has-error]="hasError('monthlyExtra')">
        <label class="field-label" for="monthlyExtra">Extra Payment Every Month <span class="optional-tag">optional</span></label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="monthlyExtra" type="number"
            [(ngModel)]="monthlyExtra"
            min="0" step="1000"
            (ngModelChange)="onFieldChange('monthlyExtra')"
            (blur)="onFieldBlur('monthlyExtra')"
            placeholder="e.g. 5,000 extra on top of your EMI" autocomplete="off"/>
        </div>
        <div class="field-hint">Add a fixed amount on top of your EMI, every month</div>
      </div>

      @if (hasError('prepayment')) {
        <div class="field-error mt-sm">{{ fieldError('prepayment') }}</div>
      }
    </div>

    <!-- Actions -->
    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [disabled]="calculating">
        @if (calculating) { <span class="spinner"></span> Calculating… }
        @else { Calculate Savings }
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">Reset</button>
      }
      @if (calcError) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Something went wrong calculating your results. Please try again.
        </span>
      }
    </div>
  </div>

  <!-- ══ RESULTS ══════════════════════════════════════════════════════════ -->
  @if (calculated && baseSummary && finalSummary) {

    <!-- Savings hero -->
    <div class="savings-banner mb-md">
      <div class="sb-item">
        <div class="sb-val">{{ fmt.currency(interestSaved) }}</div>
        <div class="sb-lbl">Interest Saved</div>
      </div>
      <div class="sb-divider"></div>
      @if (prepaymentOption === 'reduce_tenure') {
        <div class="sb-item">
          <div class="sb-val">{{ Math.floor(monthsSaved/12) }}y {{ monthsSaved%12 }}m</div>
          <div class="sb-lbl">Loan Paid Off Sooner</div>
        </div>
      } @else {
        <div class="sb-item">
          <div class="sb-val">{{ fmt.currency(emiReduced) }}</div>
          <div class="sb-lbl">⬇ Lower EMI Every Month</div>
        </div>
      }
      <div class="sb-divider"></div>
      <div class="sb-item">
        <div class="sb-val">{{ Math.floor(finalSummary.totalMonths/12) }}y {{ finalSummary.totalMonths%12 }}m</div>
        <div class="sb-lbl">New Payoff Time</div>
      </div>
    </div>

    <!-- Comparison table -->
    <div class="card mb-md">
      <div class="section-title">Without vs With Prepayment</div>
      <div class="tbl-wrap">
        <table class="compare-tbl">
          <thead>
            <tr>
              <th class="t-left">&nbsp;</th>
              <th>Without Prepayment</th>
              <th>With Prepayment</th>
              <th class="t-save">You Save</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="t-left t-year">Monthly EMI</td>
              <td>{{ fmt.currency(baseSummary.emi) }}</td>
              <td>{{ fmt.currency(finalSummary.emi) }}</td>
              <td class="t-save">{{ emiReduced > 0 ? fmt.currency(emiReduced) : '—' }}</td>
            </tr>
            <tr>
              <td class="t-left t-year">Total Interest</td>
              <td>{{ fmt.currency(baseSummary.totalInterest) }}</td>
              <td>{{ fmt.currency(finalSummary.totalInterest) }}</td>
              <td class="t-save">{{ fmt.currency(interestSaved) }}</td>
            </tr>
            <tr>
              <td class="t-left t-year">Loan Tenure</td>
              <td>{{ Math.floor(baseSummary.totalMonths/12) }}y {{ baseSummary.totalMonths%12 }}m</td>
              <td>{{ Math.floor(finalSummary.totalMonths/12) }}y {{ finalSummary.totalMonths%12 }}m</td>
              <td class="t-save">{{ monthsSaved > 0 ? (Math.floor(monthsSaved/12) + 'y ' + (monthsSaved%12) + 'm') : '—' }}</td>
            </tr>
            <tr>
              <td class="t-left t-year">Total Payment</td>
              <td>{{ fmt.currency(baseSummary.totalPayment) }}</td>
              <td>{{ fmt.currency(finalSummary.totalPayment) }}</td>
              <td class="t-save">{{ fmt.currency(Math.max(0, baseSummary.totalPayment - finalSummary.totalPayment)) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="compare-note">You put in about <strong>{{ fmt.currency(totalPrepaid) }}</strong> in prepayments and save <strong>{{ fmt.currency(interestSaved) }}</strong> in interest.</p>
    </div>

    <!-- Charts -->
    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Principal vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie"><canvas #pieChartRef></canvas></div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Yearly Payment Breakdown</div>
          <div class="chart-canvas-wrap"><canvas #barChartRef></canvas></div>
        </div>
      </div>
      <div class="chart-panel mt-sm">
        <div class="chart-panel-label">Balance Over Time — With vs Without Prepayment</div>
        <div class="chart-canvas-wrap chart-canvas-line"><canvas #lineChartRef></canvas></div>
      </div>
    </div>

    <!-- Cross-link to full EMI calculator -->
    <div class="card mb-md cross-link-card">
      <p>Want the full month-by-month amortization schedule, AI advisor and multiple prepayments? Use our
        <a routerLink="/home-loan">Home Loan EMI Calculator</a>.</p>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How does home loan prepayment save you money?</h2>
    <p>
      A home loan prepayment (also called part-payment) is any amount you pay over and above your
      regular EMI. Because Indian home loans use the reducing-balance method, every rupee you prepay
      goes straight to your outstanding <strong>principal</strong> — which lowers the interest charged
      in every single month that follows. Prepaying early in the loan, when your balance is highest,
      saves the most interest.
    </p>
    <p>
      You then choose what to do with that saving: <strong>Reduce Tenure</strong> keeps your EMI the
      same and finishes the loan years earlier, while <strong>Reduce EMI</strong> keeps the tenure the
      same and lowers your monthly payment. For most borrowers, reducing the tenure saves far more
      interest overall.
    </p>

    <h2 class="section-title mt-sm">Lump-sum vs monthly extra prepayment</h2>
    <p>
      A one-time <strong>lump-sum</strong> — say from a bonus, maturity of an FD, or a gift — makes a big
      immediate dent in your principal. A small <strong>monthly extra</strong> on top of your EMI is
      gentler on cash flow but, compounded over years, can shave off just as many years. This calculator
      lets you model both together and see the combined saving.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>Is it better to reduce tenure or reduce EMI on prepayment?</h3>
      <p>
        Reducing the tenure almost always saves more total interest, because you keep paying the same
        EMI and close the loan faster. Reducing the EMI helps monthly cash flow but leaves you paying
        for the full original tenure. Use the toggle above to compare both for your exact loan.
      </p>
    </div>
    <div class="faq-item">
      <h3>Are there charges for prepaying a home loan in India?</h3>
      <p>
        As per RBI guidelines, banks and housing finance companies cannot charge prepayment or
        foreclosure penalties on floating-rate home loans taken by individuals. Fixed-rate loans may
        attract a charge, so confirm the terms with your lender before making a large prepayment.
      </p>
    </div>
    <div class="faq-item">
      <h3>When is the best time to prepay a home loan?</h3>
      <p>
        The earlier the better. In the first years of a home loan, most of your EMI goes towards
        interest and your outstanding principal is at its highest, so an early prepayment removes the
        most future interest. Prepayments in the last few years save comparatively little.
      </p>
    </div>
    <div class="faq-item">
      <h3>How much can I save by paying one extra EMI every year?</h3>
      <p>
        Paying just one extra EMI a year (or the equivalent monthly extra) can typically cut a 20-year
        home loan by around 3–4 years and save several lakhs in interest. Enter your loan details and a
        monthly extra above to see the exact number for your loan.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/home-loan-prepayment-strategies">How to prepay a home loan: reduce EMI or reduce tenure?</a>
    </p>
  </div>
</section>
